import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const PaymentCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState('verifying');
  const [error, setError] = useState('');
  const [bookingDetails, setBookingDetails] = useState(null);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const reference = query.get('reference') || query.get('trxref');
    const jobId = query.get('job_id');
    
    console.log('🔍 Payment Callback - URL:', location.search);
    console.log('🔍 Reference:', reference);
    console.log('🔍 Job ID from URL:', jobId);
    
    if (!reference) {
      setStatus('failed');
      setError('No payment reference found.');
      return;
    }

    // ✅ If we have a job ID from the URL, fetch the job details
    if (jobId) {
      console.log('📡 Fetching job details for job ID:', jobId);
      fetchJobDetails(jobId);
      return;
    }

    // Fallback: try to get from sessionStorage
    const jobDetailsStr = sessionStorage.getItem('pendingJob');
    let jobDetails = null;
    if (jobDetailsStr) {
      try { 
        jobDetails = JSON.parse(jobDetailsStr); 
        console.log('📦 Job details from session:', jobDetails);
      } catch(e) {
        console.error('Error parsing job details:', e);
      }
    }

    verifyPayment(reference, jobDetails);
  }, [location]);

  const fetchJobDetails = async (jobId) => {
    try {
      const token = localStorage.getItem('token');
      console.log('🔑 Fetching job with token:', token ? 'Present' : 'Missing');
      
      const response = await fetch(`/api/jobs/${jobId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('📡 Response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('📊 Job data received:', data);
      
      const job = data.job || data;
      
      setStatus('success');
      setBookingDetails({
        jobId: job.id || jobId,
        pickup: job.pickup_address || 'N/A',
        dropoff: job.dropoff_address || 'N/A',
        amount: job.price_cents ? (job.price_cents / 100).toFixed(2) : '0.00',
        pin: job.verification_pin || 'N/A',
        status: job.status || 'confirmed'
      });
      
    } catch (err) {
      console.error('❌ Error fetching job details:', err);
      setStatus('failed');
      setError('Failed to fetch job details. Please check your dashboard.');
    }
  };

  const verifyPayment = async (reference, jobDetails) => {
    const customerId = localStorage.getItem('userId');
    
    try {
      const response = await fetch('/api/jobs/verify-payment', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ 
          reference, 
          customer_id: customerId,
          job_id: jobDetails?.jobId || jobDetails?.id
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setStatus('success');
        const job = data.job || data;
        setBookingDetails({
          jobId: job.id || jobDetails?.jobId || jobDetails?.id || 'N/A',
          pickup: job.pickup_address || jobDetails?.pickup_address || 'N/A',
          dropoff: job.dropoff_address || jobDetails?.dropoff_address || 'N/A',
          amount: job.price_cents ? (job.price_cents / 100).toFixed(2) : jobDetails?.amount || '0.00',
          pin: job.verification_pin || jobDetails?.pin || 'N/A',
          status: job.status || 'confirmed'
        });
        sessionStorage.removeItem('pendingJob');
      } else {
        setStatus('failed');
        setError(data.message || 'Payment verification failed.');
      }
    } catch (err) {
      console.error('Payment verification error:', err);
      setStatus('failed');
      setError('Network error while verifying payment.');
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: '#111827', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      padding: '20px' 
    }}>
      <div style={{ 
        backgroundColor: '#1f2937', 
        padding: '40px', 
        borderRadius: '12px', 
        textAlign: 'center', 
        border: '2px solid #eab308', 
        maxWidth: '500px', 
        width: '100%' 
      }}>
        {status === 'verifying' && (
          <>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
            <h2 style={{ color: '#eab308' }}>Verifying Payment...</h2>
            <p style={{ color: '#d1d5db' }}>Please wait while we confirm your transaction.</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>✅</div>
            <h2 style={{ color: '#eab308' }}>Payment Successful!</h2>
            <p style={{ color: '#d1d5db', marginBottom: '20px' }}>Your delivery has been booked successfully.</p>
            
            <div style={{ 
              backgroundColor: '#111827', 
              borderRadius: '10px', 
              padding: '20px',
              marginBottom: '20px',
              border: '1px solid #374151',
              textAlign: 'left'
            }}>
              <h4 style={{ color: '#eab308', marginBottom: '15px', textAlign: 'center' }}>
                📋 Booking Confirmation
              </h4>
              
              <div style={{ marginBottom: '10px' }}>
                <div style={{ color: '#9ca3af', fontSize: '11px', fontWeight: 'bold' }}>
                  PICKUP
                </div>
                <div style={{ color: '#d1d5db', fontSize: '13px' }}>
                  {bookingDetails?.pickup || 'N/A'}
                </div>
              </div>

              <div style={{ marginBottom: '10px' }}>
                <div style={{ color: '#9ca3af', fontSize: '11px', fontWeight: 'bold' }}>
                  DROPOFF
                </div>
                <div style={{ color: '#d1d5db', fontSize: '13px' }}>
                  {bookingDetails?.dropoff || 'N/A'}
                </div>
              </div>

              {bookingDetails?.pin && bookingDetails.pin !== 'N/A' && (
                <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #374151' }}>
                  <div style={{ color: '#9ca3af', fontSize: '11px', fontWeight: 'bold' }}>
                    SAFETY PIN
                  </div>
                  <div style={{ 
                    color: '#eab308', 
                    fontSize: '18px', 
                    fontWeight: 'bold',
                    fontFamily: 'monospace',
                    letterSpacing: '4px'
                  }}>
                    {bookingDetails?.pin}
                  </div>
                </div>
              )}

              <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #374151' }}>
                <div style={{ color: '#9ca3af', fontSize: '11px', fontWeight: 'bold' }}>
                  STATUS
                </div>
                <div style={{ 
                  color: '#34d399', 
                  fontSize: '14px', 
                  fontWeight: 'bold'
                }}>
                  ✅ {bookingDetails?.status?.toUpperCase() || 'CONFIRMED'}
                </div>
              </div>
            </div>

            <div style={{
              backgroundColor: 'rgba(230, 126, 34, 0.1)',
              border: '1px solid rgba(230, 126, 34, 0.3)',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '20px'
            }}>
              <p style={{ color: '#fbbf24', fontSize: '13px', margin: 0 }}>
                <i className="fas fa-info-circle"></i>
                <strong> 📝 Please check your email</strong> for booking confirmation and details.
              </p>
            </div>

            <button 
              onClick={() => navigate('/customer/dashboard')} 
              style={{ 
                backgroundColor: '#eab308', 
                color: 'black', 
                border: 'none', 
                padding: '12px 30px', 
                borderRadius: '6px', 
                cursor: 'pointer', 
                fontWeight: 'bold',
                fontSize: '15px',
                transition: '0.3s'
              }}
              onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
            >
              Go to Dashboard →
            </button>
          </>
        )}
        
        {status === 'failed' && (
          <>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>❌</div>
            <h2 style={{ color: '#ef4444' }}>Payment Failed</h2>
            <p style={{ color: '#d1d5db' }}>{error}</p>
            <button 
              onClick={() => navigate('/customer/dashboard')} 
              style={{ 
                backgroundColor: '#eab308', 
                color: 'black', 
                border: 'none', 
                padding: '12px 24px', 
                borderRadius: '6px', 
                cursor: 'pointer', 
                fontWeight: 'bold',
                marginTop: '15px'
              }}
            >
              Back to Dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentCallback;
